import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styles from './StudentEvaluationDetail.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import axiosClient from "../../../api/axiosClient";
import GraduationProfile from "../../../components/graduationProfile/GraduationProfile";
import GraduationTimeline from "../../../components/graduationTimeline/GraduationTimeline";
import CategoryPerformance from "../../../components/categoryPerformance/CategoryPerformance";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import InformationCard from "../../../components/informationCard/InformationCard";
import { Tab, TabPanel, Tabs } from "../../../components/tabs/Tabs";
import EvaluationStudentCard from "../../../components/evaluationStudentCard/EvaluationStudentCard";
import LoadMore from "../../../components/loadMore/loadMore";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { calculateTimeDifference, getDateOnly } from "../../../utils/timeUtils";
import { format } from "date-fns";
import EvaluationRequestStudentCard from "../../../components/evaluationRequestStudentCard/EvaluationRequestStudentCard";
import Filter from "../../../components/filter/Filter";
import CustomDateRangePicker from "../../../components/customDateRangePicker/CustomDateRangePicker";

const StudentEvaluationDetail = () => {
  const { studentId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    overview: {},
    timeline: [],
    competencyBasePerformance: [],
    specialityBasePerformance: [],
    instructorBasePerformance: [],
    clinicalSiteBasePerformance: [],
    personalInformation: {},
    assignmentDetails: {},
  });
  const { showToast } = useToasts();
  const limit = 20;

  /* Requests Tab */
  const [requests, setRequests] = useState([]);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsCount, setRequestsCount] = useState(0);
  const [requestsTotalPages, setRequestsTotalPages] = useState(1);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  
  const [selectedFilter, setSelectedFilter] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 6)));
  const [endDate, setEndDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ startDate, endDate });

  /* Pending Tab */
  const [isPendingLoading, setIsPendingLoading] = useState(false);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingFilter, setPendingFilter] = useState('Pending');
  const [pendingEvaluations, setPendingEvaluations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  
  /* Completed Tab */
  const [isCompletedLoading, setIsCompletedLoading] = useState(false);
  const [completedFilter, setCompletedFilter] = useState('Completed');
  const [completedPage, setCompletedPage] = useState(1);
  const [completedEvaluations, setCompletedEvaluations] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [completedTotalPages, setCompletedTotalPages] = useState(1);

  useEffect(() => {
    if(startDate && endDate) {
      setDateRange({ startDate, endDate });
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const fetchRequestsData = async () => {
      if(requestsPage === 1) {
        setIsRequestsLoading(true);
      }
      const params = {
        limit: limit,
        page: requestsPage,
        studentId: studentId,
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
      };
      if(selectedFilter) {
        params.filter = selectedFilter;
      }
      await axiosClient.get(`evaluation-requests`, {
        params: params
      })
      .then ((response) => {
        if(response.data?.evaluationRequests) {
          if(requestsPage === 1) {
            setRequests([]);
          }
          setRequests((prevData) => {
            return [...prevData, ...response.data.evaluationRequests.map((request) => {
              return {
                id: request.id,
                date: request.date,
                data: [
                  {
                    label: 'Instructor',
                    text: `${request?.instructor?.firstName} ${request?.instructor?.lastName}` 
                  },
                  {
                    label: 'Clinical Site',
                    text: request?.clinicalSite?.name ?? ''
                  },
                  {
                    label: 'Request ID',
                    text: request.id
                  }
                ],
                status: request.status
              }
            })];
          });
          setRequestsCount(response.data.total)
          setRequestsTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsRequestsLoading(false);
      });
    }

    if(studentId) {
      fetchRequestsData();
    }
  }, [requestsPage, selectedFilter, showToast, dateRange, studentId]);

  useEffect(() => {
    // Reset to page 1 when selectedFilter changes
    setRequestsPage(1);
  }, [selectedFilter]);

  const requestsLoadMore = async () => {
    setRequestsPage(requestsPage + 1);
  };

  useEffect(() => {
    const fetchPendingData = async () => {
      setIsPendingLoading(true);
      await axiosClient.get(`evaluations`, {
        params: {
          status: pendingFilter,
          limit: limit,
          page: pendingPage,
          studentId: studentId
        }
      })
      .then ((response) => {
        if(response.data?.evaluations) {
          if(pendingPage === 1) {
            setPendingEvaluations([]);
          }
          setPendingEvaluations((prevData) => {
            return [...prevData, ...response.data.evaluations.map((evaluation) => {
              const { isExpired, hours, timeDifference } = calculateTimeDifference(evaluation.missedDate);
              return {
                evaluationId: evaluation.id.toString(),
                timeRemaining: isExpired ? evaluation.status : timeDifference,
                timeColor: isExpired || hours < 2 ? 'red' : 'green',
                totalCount: evaluation.total,
                completedCount: evaluation.completed,
                isExpired: evaluation.status === 'Expired',
              };
            })];
          });
          setPendingCount(response.data.total);
          setPendingTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsPendingLoading(false);
      });
    }

    if(studentId) {
      fetchPendingData();
    }
  }, [pendingPage, pendingFilter, showToast, studentId]);

  useEffect(() => {
    setPendingPage(1);
    setPendingEvaluations([]);
  }, [pendingFilter]);

  const pendingLoadMore = async () => {
    setPendingPage(pendingPage + 1);
  };

  useEffect(() => {
    const fetchCompletedData = async () => {
      setIsCompletedLoading(true);
      await axiosClient.get(`evaluations`, {
        params: {
          status: completedFilter,
          limit: limit,
          page: completedPage,
          studentId: studentId
        }
      })
      .then ((response) => {
        if(response.data?.evaluations) {
          if(completedPage === 1) {
            setCompletedEvaluations([]);
          }
          setCompletedEvaluations((prevData) => {
            return [...prevData, ...response.data.evaluations.map((evaluation) => {
              return {
                evaluationId: evaluation.id.toString(),
                completedOn: getDateOnly(evaluation.completedDate),
                totalCount: evaluation.total,
                completedCount: evaluation.completed,
                isLate: evaluation.status === 'Late Submitted',
              }
            })];
          });
          setCompletedCount(response.data.total);
          setCompletedTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsCompletedLoading(false);
      });
    }

    if(studentId) {
      fetchCompletedData();
    }
  }, [completedPage, completedFilter, showToast, studentId]);

  useEffect(() => {
    setCompletedPage(1);
    setCompletedEvaluations([]);
  }, [completedFilter]);

  const completedLoadMore = async () => {
    setCompletedPage(completedPage + 1);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axiosClient.get(`evaluations/students/${studentId}/graduation-summary`);
        setData(response.data);
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (studentId) {
      fetchData();
    }
  }, [studentId, showToast]);

  const competencyData = data?.competencyBasePerformance?.map((item, index) => ({
    name: item.tag,
    cumulativeScore: item.cumulativeScore,
    variant: (index % 6) + 1,
  })) || [];

  const specialityData = data?.specialityBasePerformance?.map(item => ({
    name: item.speciality.name,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const instructorData = data?.instructorBasePerformance?.map(item => ({
    name: `${item.instructor.firstName} ${item.instructor.lastName}`,
    firstName: item.instructor.firstName,
    lastName: item.instructor.lastName,
    profilePicture: item.instructor.profilePicture,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const clinicalSiteData = data?.clinicalSiteBasePerformance?.map(item => ({
    name: item.clinicalSite.name,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const cumulativeViews = [
    { label: 'Competency', value: 'competency', data: competencyData, type: 'tag', itemsPerRow: 4 },
    { label: 'Specialty', value: 'specialty', data: specialityData, type: 'name', itemsPerRow: 5 },
    { label: 'Instructor', value: 'instructor', data: instructorData, type: 'instructor', itemsPerRow: 4 },
    { label: 'Clinical Site', value: 'clinicalSite', data: clinicalSiteData, type: 'name', itemsPerRow: 5 },
  ];

  // Map timeline data for GraduationTimeline
  const timelineData = data?.timeline?.map((item) => {
    const statusMap = {
      'COMPLETED': 'Completed',
      'IN_PROGRESS': 'In Progress',
      'NOT_STARTED': 'Not Started',
    };
    const classMap = {
      'COMPLETED': 'completed',
      'IN_PROGRESS': 'inProgress',
      'NOT_STARTED': 'notStarted',
    };

    return {
      year: item.year.year,
      status: statusMap[item.status] || 'Not Started',
      statusClass: classMap[item.status] || 'notStarted',
      cumulativeScore: item.cumulativeScore || '0',
      scorePercentage: item.cumulativeScore ? Math.round(item.cumulativeScore * 20) : 0,
      scoreColor: item.status === 'COMPLETED' ? 'green' : item.status === 'IN_PROGRESS' ? 'red' : 'grey',
    };
  }) || [];

  const { overview } = data;

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbHeader}>
        <div className={styles.breadcrumbs}>
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Evaluations', path: '/evaluations' },
              { label: data?.overview?.student?.firstName && data?.overview?.student?.lastName ? `${data?.overview?.student?.firstName} ${data?.overview?.student?.lastName}` : 'Student Evaluation Detail' }
            ]}
          />
        </div>
      </div>
      <Tabs defaultExpanded>
        <Tab>
          <span>About</span>
        </Tab>
        <Tab>
          <span>Pending</span>
          {pendingCount ? <span className={styles.count}>{pendingCount}</span> : ''}
        </Tab>
        <Tab>
          <span>Completed</span>
          {completedCount ? <span className={styles.count}>{completedCount}</span> : ''}
        </Tab>
        <Tab>
          <span>Requests</span>
          {requestsCount ? <span className={styles.count}>{requestsCount}</span> : ''}
        </Tab>

        <TabPanel>
          <div className={styles.container}>
            <GraduationProfile overview={overview} />
            <GraduationTimeline data={timelineData} minimumScore={overview?.minimumScore} />
            <CategoryPerformance
              views={cumulativeViews}
              title="Cumulative Score Summary"
            />
            {data?.personalInformation && data?.assignmentDetails && (
              <InformationCard
                title="Personal & Clinical Assignment Information"
                items={[
                  { label: 'Email', value: data?.personalInformation?.email },
                  { label: 'Phone', value: data?.personalInformation?.mobileNumber },
                  { label: 'Date of Birth', value: data?.personalInformation?.dateOfBirth },
                  {
                    label: 'Specialty',
                    value: data?.assignmentDetails?.specialities && data.assignmentDetails.specialities.length > 0
                      ? data.assignmentDetails.specialities.map(s => s.name).join(', ')
                      : undefined
                  },
                  {
                    label: 'Instructor',
                    value: data?.assignmentDetails?.instructor
                      ? `${data.assignmentDetails.instructor.firstName} ${data.assignmentDetails.instructor.lastName}`
                      : undefined
                  },
                ]}
                itemsPerRow={3}
              />
            )}
          </div>
        </TabPanel>
        <TabPanel>
          <div className={styles.container}>
            <Filter
              filters={[
                { label: 'All', value: 'Pending' },
                { label: 'Missed', value: 'Missed' },
              ]}
              selected={pendingFilter}
              onChange={setPendingFilter}
            />
            { pendingEvaluations.length > 0 ? <div className={styles.cardList}>
              {pendingEvaluations.map((item) => {
                return (
                  <div className={styles.cardItem} key={`pending-${item.evaluationId}`}>
                    <EvaluationStudentCard
                      data={[
                        { label: 'Evaluation ID', text: item.evaluationId },
                        { label: 'Time Remaining', text: item.timeRemaining, color: item.timeColor },
                      ]}
                      count={{
                        total: item.totalCount,
                        completed: item.completedCount
                      }}
                      disabled={item.isExpired}
                    />
                  </div>
                );
              })}
              {(isPendingLoading || pendingPage < pendingTotalPages) 
                && <LoadMore isLoading={isPendingLoading} onClick={pendingLoadMore} />}
            </div> : !isPendingLoading ? <div className={styles.emptyEvaluations}>
              <p>You have no pending evaluation.</p>
            </div> : <LoadingSpinner size={33} />}
          </div>
        </TabPanel>
        <TabPanel>
          <div className={styles.container}>
            <Filter
              filters={[
                { label: 'All', value: 'Completed' },
                { label: 'Late', value: 'Late' },
              ]}
              selected={completedFilter}
              onChange={setCompletedFilter}
            />
            { completedEvaluations.length > 0 ? <div className={styles.cardList}>
              {completedEvaluations.map((item) => {
                return (
                  <div className={styles.cardItem} key={`completed-${item.evaluationId}`}>
                    <EvaluationStudentCard
                      data={[
                        { label: 'Evaluation ID', text: item.evaluationId },
                        { label: 'Submitted Date', text: format(item.completedOn, 'MM/dd/yyyy'), tag: item.isLate ? 'Late' : undefined },
                      ]}
                      count={{
                        total: item.totalCount,
                        completed: item.completedCount
                      }}
                      cta={{
                        label: 'View Score',
                        link: `/evaluations/${item.evaluationId}/summary`
                      }}
                    />
                  </div>
                );
              })}
              {(isCompletedLoading || completedPage < completedTotalPages) 
                && <LoadMore isLoading={isCompletedLoading} onClick={completedLoadMore} />}
            </div> : !isCompletedLoading ? <div className={styles.emptyEvaluations}>
              <p>You have no completed evaluations at this time.</p>
            </div> : <LoadingSpinner size={33} />}
          </div>
        </TabPanel>
        <TabPanel>
          <div className={styles.container}>
            <div className={styles.topBar}>
              <div className={styles.filterWrapper}>
              <Filter
                filters={[
                  {
                    label: 'All',
                    value: '',
                  },
                  {
                    label: 'Accepted',
                    value: 'Accepted',
                  },
                  {
                    label: 'Declined',
                    value: 'Declined',
                  }
                ]}
                selected={selectedFilter}
                onChange={(filter) => {
                  setSelectedFilter(filter);
                }}
              />
              </div>
              <CustomDateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
              />
            </div>
            { requests.length > 0 ? <div className={styles.cardList}>
              {requests.map((request, index) => {
                return (
                  <div className={styles.cardItem} key={`request-${index}`}>
                    <EvaluationRequestStudentCard
                      {...request}
                    />
                  </div>
                );
              })}
              {(isRequestsLoading || requestsPage < requestsTotalPages) 
                && <LoadMore isLoading={isRequestsLoading} onClick={requestsLoadMore} />}
            </div> : !isRequestsLoading ? <div className={styles.emptyEvaluations}>
              <p>No evaluation requests found.</p>
            </div> : <LoadingSpinner size={33} />}
          </div>
        </TabPanel>
      </Tabs>
    </>
  );
};

export default StudentEvaluationDetail;
